import React, { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { FiEdit2, FiTrash2, FiPlus, FiSave, FiLoader } from "react-icons/fi";

const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5000";


const AdminCareGuide = () => {
  const [loading, setLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [data, setData] = useState({
    heroTitle: "",
    heroSubtitle: "",
    introText: "",
    careTips: [],
    materials: [],
    expertAdvice: "",
    expertAuthor: "",
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get(`${apiUrl}/api/care-guide`);
        if (res.data) {
          const updatedTips = res.data.careTips.map((tip) => ({
            ...tip,
            imagePreview: null,
            imageFile: null,
          }));
          setData({ ...res.data, careTips: updatedTips });
        }
      } catch (error) {
        setErrorMsg("Erreur lors du chargement des données.");
        console.error(error);
      }
    };

    fetchData();
  }, []);

  const handleChange = (field, value) => {
    setData((prev) => ({ ...prev, [field]: value }));
  };

  const handleCareTipChange = (index, field, value) => {
    const updatedTips = [...data.careTips];
    updatedTips[index] = { ...updatedTips[index], [field]: value };
    setData((prev) => ({ ...prev, careTips: updatedTips }));
  };

  const handleCareTipTipChange = (tipIndex, i, value) => {
    const updatedTips = [...data.careTips];
    const updatedTipList = [...(updatedTips[tipIndex].tips || [])];
    updatedTipList[i] = value;
    updatedTips[tipIndex].tips = updatedTipList;
    setData((prev) => ({ ...prev, careTips: updatedTips }));
  };

  const handleCareTipImageChange = (index, file) => {
    const updatedTips = [...data.careTips];
    updatedTips[index].imageFile = file;
    updatedTips[index].imagePreview = file ? URL.createObjectURL(file) : null;
    setData((prev) => ({ ...prev, careTips: updatedTips }));
  };

  const addCareTip = () => {
    setData((prev) => ({
      ...prev,
      careTips: [
        ...prev.careTips,
        { title: "", tips: [""], imageFile: null, imagePreview: null },
      ],
    }));
  };

  const removeCareTip = (index) => {
    const updatedTips = [...data.careTips];
    updatedTips.splice(index, 1);
    setData((prev) => ({ ...prev, careTips: updatedTips }));
  };

  const addTipToCareTip = (index) => {
    const updatedTips = [...data.careTips];
    updatedTips[index].tips = [...(updatedTips[index].tips || []), ""];
    setData((prev) => ({ ...prev, careTips: updatedTips }));
  };

  const removeTipFromCareTip = (tipIndex, tipItemIndex) => {
    const updatedTips = [...data.careTips];
    updatedTips[tipIndex].tips = updatedTips[tipIndex].tips.filter(
      (_, i) => i !== tipItemIndex
    );
    setData((prev) => ({ ...prev, careTips: updatedTips }));
  };

  const handleMaterialChange = (index, field, value) => {
    const updatedMaterials = [...data.materials];
    updatedMaterials[index] = { ...updatedMaterials[index], [field]: value };
    setData((prev) => ({ ...prev, materials: updatedMaterials }));
  };

  const addMaterial = () => {
    setData((prev) => ({
      ...prev,
      materials: [...prev.materials, { name: "", care: "" }],
    }));
  };

  const removeMaterial = (index) => {
    const updatedMaterials = [...data.materials];
    updatedMaterials.splice(index, 1);
    setData((prev) => ({ ...prev, materials: updatedMaterials }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSuccessMsg("");
    setErrorMsg("");

    try {
      const formData = new FormData();

      const payload = {
        heroTitle: data.heroTitle,
        heroSubtitle: data.heroSubtitle,
        introText: data.introText,
        materials: data.materials,
        expertAdvice: data.expertAdvice,
        expertAuthor: data.expertAuthor,
        careTips: data.careTips.map((tip, i) => {
          const cleanTip = {
            title: tip.title,
            tips: tip.tips,
            imageUrl: tip.imageUrl || null,
          };
          if (tip.imageFile) {
            formData.append(`image_${i}`, tip.imageFile);
          }
          return cleanTip;
        }),
      };

      formData.append("data", JSON.stringify(payload));

      await axios.post(`${apiUrl}/api/care-guide`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      setSuccessMsg("Guide mis à jour avec succès !");
      setTimeout(() => setSuccessMsg(""), 3000);
    } catch (err) {
      setErrorMsg(
        "Erreur lors de la mise à jour : " +
          (err.response?.data?.message || err.message)
      );
      setTimeout(() => setErrorMsg(""), 5000);
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="admin-container"
    >
      <div className="admin-header">
        <FiEdit2 className="header-icon" />
        <h2>Modifier le Guide d'Entretien SAFA</h2>
      </div>

      <form onSubmit={handleSubmit} className="admin-form">
        {/* HERO SECTION */}
        <div className="form-section">
          <h3 className="section-title">Section Hero</h3>
          <div className="form-group">
            <label>Titre Hero</label>
            <input
              type="text"
              value={data.heroTitle}
              onChange={(e) => handleChange("heroTitle", e.target.value)}
              required
            />
          </div>
          <div className="form-group">
            <label>Sous-titre Hero</label>
            <input
              type="text"
              value={data.heroSubtitle}
              onChange={(e) => handleChange("heroSubtitle", e.target.value)}
              required
            />
          </div>
          <div className="form-group">
            <label>Introduction</label>
            <textarea
              value={data.introText}
              onChange={(e) => handleChange("introText", e.target.value)}
              required
            />
          </div>
        </div>

        {/* CARE TIPS SECTION */}
        <div className="form-section">
          <div className="section-header">
            <h3 className="section-title">Conseils d'Entretien</h3>
            <button type="button" onClick={addCareTip} className="add-button">
              <FiPlus /> Ajouter un conseil
            </button>
          </div>

          {data.careTips.map((tip, index) => (
            <div key={index} className="card">
              <div className="form-group">
                <label>Titre du conseil</label>
                <input
                  type="text"
                  value={tip.title || ""}
                  onChange={(e) =>
                    handleCareTipChange(index, "title", e.target.value)
                  }
                  required
                />
              </div>

              <div className="form-group">
                <label>Points clés</label>
                {tip.tips.map((t, i) => (
                  <div key={i} className="tip-item">
                    <input
                      type="text"
                      value={t}
                      onChange={(e) =>
                        handleCareTipTipChange(index, i, e.target.value)
                      }
                      required
                    />
                    <button
                      type="button"
                      onClick={() => removeTipFromCareTip(index, i)}
                      className="remove-item-button"
                    >
                      <FiTrash2 />
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => addTipToCareTip(index)}
                  className="add-subitem-button"
                >
                  <FiPlus /> Ajouter un point
                </button>
              </div>

              <div className="form-group">
                <label>Image illustrative</label>
                {tip.imagePreview || tip.imageUrl ? (
                  <div className="image-preview-container">
                    <img
                      src={tip.imagePreview || `${apiUrl}${tip.imageUrl}`}
                      alt="aperçu"
                      className="image-preview"
                    />
                    <button
                      type="button"
                      onClick={() => handleCareTipImageChange(index, null)}
                      className="remove-image-button"
                    >
                      <FiTrash2 /> Supprimer
                    </button>
                  </div>
                ) : null}
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) =>
                    handleCareTipImageChange(index, e.target.files[0])
                  }
                  className="file-input"
                />
              </div>

              <button
                type="button"
                onClick={() => removeCareTip(index)}
                className="remove-button"
              >
                <FiTrash2 /> Supprimer ce conseil
              </button>
            </div>
          ))}
        </div>

        {/* MATERIALS SECTION */}
        <div className="form-section">
          <div className="section-header">
            <h3 className="section-title">Entretien des Matériaux</h3>
            <button type="button" onClick={addMaterial} className="add-button">
              <FiPlus /> Ajouter un matériau
            </button>
          </div>

          {data.materials.map((mat, index) => (
            <div key={index} className="card">
              <div className="form-group">
                <label>Nom du matériau</label>
                <input
                  type="text"
                  value={mat.name}
                  onChange={(e) =>
                    handleMaterialChange(index, "name", e.target.value)
                  }
                  required
                />
              </div>
              <div className="form-group">
                <label>Conseil d'entretien</label>
                <input
                  type="text"
                  value={mat.care}
                  onChange={(e) =>
                    handleMaterialChange(index, "care", e.target.value)
                  }
                  required
                />
              </div>
              <button
                type="button"
                onClick={() => removeMaterial(index)}
                className="remove-button"
              >
                <FiTrash2 /> Supprimer ce matériau
              </button>
            </div>
          ))}
        </div>

        {/* EXPERT SECTION */}
        <div className="form-section">
          <h3 className="section-title">Conseil d'Expert</h3>
          <div className="form-group">
            <label>Conseil</label>
            <textarea
              value={data.expertAdvice}
              onChange={(e) => handleChange("expertAdvice", e.target.value)}
              required
            />
          </div>
          <div className="form-group">
            <label>Auteur du conseil</label>
            <input
              type="text"
              value={data.expertAuthor}
              onChange={(e) => handleChange("expertAuthor", e.target.value)}
              required
            />
          </div>
        </div>

        <motion.button
          type="submit"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          disabled={loading}
          className="submit-button"
        >
          {loading ? (
            <>
              <FiLoader className="spin" /> Enregistrement...
            </>
          ) : (
            <>
              <FiSave /> Enregistrer les modifications
            </>
          )}
        </motion.button>
      </form>

      {successMsg && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="success-message"
        >
          {successMsg}
        </motion.div>
      )}

      {errorMsg && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="error-message"
        >
          {errorMsg}
        </motion.div>
      )}

      <style jsx>{`
        .admin-container {
          max-width: 900px;
          margin: 2rem auto;
          padding: 2rem;
          background: #fff;
          border-radius: 16px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
          font-family: "Inter", -apple-system, sans-serif;
        }

        .admin-header {
          display: flex;
          align-items: center;
          gap: 1rem;
          margin-bottom: 2.5rem;
        }

        .admin-header h2 {
          margin: 0;
          font-size: 1.75rem;
          color: #2d3748;
        }

        .header-icon {
          font-size: 1.75rem;
          color: #4a5568;
        }

        .admin-form {
          display: flex;
          flex-direction: column;
          gap: 2.5rem;
        }

        .form-section {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }

        .section-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 1rem;
        }

        .section-title {
          margin: 0;
          font-size: 1.25rem;
          color: #4a5568;
          font-weight: 600;
        }

        .card {
          padding: 1.5rem;
          border-radius: 12px;
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          display: flex;
          flex-direction: column;
          gap: 1.25rem;
        }

        .form-group {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        label {
          font-weight: 500;
          color: #4a5568;
          font-size: 0.95rem;
        }

        input,
        textarea {
          padding: 0.75rem 1rem;
          font-size: 1rem;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          transition: all 0.2s;
          background: #fff;
        }

        input:focus,
        textarea:focus {
          outline: none;
          border-color: #4299e1;
          box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.1);
        }

        textarea {
          min-height: 100px;
          resize: vertical;
        }

        .tip-item {
          display: flex;
          gap: 0.5rem;
        }

        .tip-item input {
          flex: 1;
        }

        .remove-item-button {
          background: #fed7d7;
          color: #e53e3e;
          border: none;
          border-radius: 6px;
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s;
        }

        .remove-item-button:hover {
          background: #feb2b2;
        }

        .image-preview-container {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
          margin-bottom: 0.5rem;
        }

        .image-preview {
          max-width: 200px;
          max-height: 150px;
          border-radius: 8px;
          border: 1px solid #e2e8f0;
        }

        .remove-image-button {
          background: #fed7d7;
          color: #e53e3e;
          border: none;
          border-radius: 6px;
          padding: 0.5rem 1rem;
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          cursor: pointer;
          transition: all 0.2s;
          width: fit-content;
        }

        .remove-image-button:hover {
          background: #feb2b2;
        }

        .file-input {
          padding: 0.5rem 0;
        }

        .add-button {
          background: #4299e1;
          color: white;
          border: none;
          border-radius: 8px;
          padding: 0.5rem 1rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          cursor: pointer;
          transition: all 0.2s;
        }

        .add-button:hover {
          background: #3182ce;
        }

        .add-subitem-button {
          background: #e2e8f0;
          color: #4a5568;
          border: none;
          border-radius: 6px;
          padding: 0.5rem 1rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          cursor: pointer;
          transition: all 0.2s;
          margin-top: 0.5rem;
          font-size: 0.9rem;
        }

        .add-subitem-button:hover {
          background: #cbd5e0;
        }

        .remove-button {
          background: #fed7d7;
          color: #e53e3e;
          border: none;
          border-radius: 8px;
          padding: 0.5rem 1rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          cursor: pointer;
          transition: all 0.2s;
          width: fit-content;
        }

        .remove-button:hover {
          background: #feb2b2;
        }

        .submit-button {
          background: #38a169;
          color: white;
          border: none;
          border-radius: 8px;
          padding: 1rem 2rem;
          font-size: 1.1rem;
          font-weight: 500;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.75rem;
          cursor: pointer;
          transition: all 0.2s;
          width: 100%;
          margin-top: 1rem;
        }

        .submit-button:hover:not(:disabled) {
          background: #2f855a;
        }

        .submit-button:disabled {
          background: #a0aec0;
          cursor: not-allowed;
        }

        .spin {
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        .success-message {
          background: #f0fff4;
          color: #38a169;
          padding: 1rem;
          border-radius: 8px;
          margin-top: 1.5rem;
          border: 1px solid #c6f6d5;
        }

        .error-message {
          background: #fff5f5;
          color: #e53e3e;
          padding: 1rem;
          border-radius: 8px;
          margin-top: 1.5rem;
          border: 1px solid #fed7d7;
        }
      `}</style>
    </motion.div>
  );
};

export default AdminCareGuide;
